import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loading, Card, EmptyState } from "@/components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { GitBranch, Plus, Copy, ArrowUp, ArrowDown, Trash2, Eye, Power, PowerOff, ArrowRight, CheckCircle2, Circle } from "lucide-react";

const inputCls = "h-10 w-full rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic";
const primaryBtn = "h-10 px-4 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700 inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50";
const ghostBtn = "h-9 px-3 rounded-lg border border-brand-line text-sm font-semibold text-brand-navy hover:bg-brand-bg inline-flex items-center gap-1.5";

// Steps the fixed workflow engine actually supports (status keys).
const SUPPORTED = [
  { key: "draft", label: "Draft", is_approval: false },
  { key: "submitted", label: "Submitted", is_approval: false },
  { key: "in_review", label: "In Review", is_approval: false },
  { key: "approved", label: "Approved", is_approval: true },
  { key: "rejected", label: "Returned for Revision", is_approval: true },
  { key: "denied", label: "Denied", is_approval: true },
];
const SUPPORTED_LABEL = Object.fromEntries(SUPPORTED.map((s) => [s.key, s.label]));

export function WorkflowTemplatesTab() {
  const [types, setTypes] = useState(null);
  const [editing, setEditing] = useState(null);   // type object being edited
  const [creating, setCreating] = useState(false);
  const [dup, setDup] = useState(null);           // type to duplicate

  const load = () => api.get("/insight-types?include_inactive=true").then((r) => setTypes(r.data.insight_types)).catch(() => setTypes(false));
  useEffect(() => { load(); }, []);

  if (types === null) return <Loading />;
  if (types === false) return <EmptyState icon={GitBranch} title="Unable to load workflows" />;
  if (editing) return <WorkflowEditor type={editing} onClose={() => { setEditing(null); load(); }} />;

  return (
    <div data-testid="workflow-templates">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 max-w-2xl">Workflow templates define how a government item moves from Creator to Finalization. Editing a workflow creates a new version — existing items keep the version they started with.</p>
        <button data-testid="create-workflow-btn" onClick={() => setCreating(true)} className={primaryBtn}><Plus className="h-4 w-4" /> Create Workflow</button>
      </div>
      <div className="space-y-3">
        {types.map((t) => (
          <Card key={t.type_id} data-testid={`workflow-${t.type_id}`} className="!p-4">
            <div className="flex flex-wrap items-start gap-3">
              <span className="h-9 w-9 rounded-lg bg-brand-civic-50 flex items-center justify-center shrink-0"><GitBranch className="h-4 w-4 text-brand-civic" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-semibold text-brand-ink">{t.name}</h3>
                  <span className="text-xs font-semibold text-slate-400">Version {t.workflow_template?.version ?? 1}</span>
                  <span data-testid={`workflow-status-${t.type_id}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${t.active ? "bg-brand-teal-50 text-brand-teal border-brand-teal/30" : "bg-slate-100 text-slate-500 border-slate-300"}`}>{t.active ? "ACTIVE" : "RETIRED"}</span>
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{t.description || "No description."}</p>
                <p className="text-xs text-slate-400 mt-1"><span className="font-medium text-slate-600">{t.used_by_count ?? 0}</span> item(s) use this workflow</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button data-testid={`workflow-view-${t.type_id}`} onClick={() => setEditing(t)} className={ghostBtn}><Eye className="h-3.5 w-3.5" /> View / Edit</button>
                <button data-testid={`workflow-duplicate-${t.type_id}`} onClick={() => setDup(t)} className={ghostBtn}><Copy className="h-3.5 w-3.5" /> Duplicate</button>
                <RetireButton t={t} onDone={load} />
              </div>
            </div>
            <WorkflowPreview steps={t.workflow_template?.steps} compact />
          </Card>
        ))}
      </div>
      {creating && <CreateWorkflowDialog onClose={() => { setCreating(false); load(); }} />}
      {dup && <DuplicateDialog type={dup} onClose={() => { setDup(null); load(); }} />}
    </div>
  );
}

function RetireButton({ t, onDone }) {
  const toggle = async () => {
    try {
      await api.put(`/insight-types/${t.type_id}`, { active: !t.active });
      toast.success(t.active ? "Workflow retired" : "Workflow reactivated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
  };
  return (
    <button data-testid={`workflow-retire-${t.type_id}`} onClick={toggle} className={ghostBtn}>
      {t.active ? <><PowerOff className="h-3.5 w-3.5" /> Retire</> : <><Power className="h-3.5 w-3.5" /> Reactivate</>}
    </button>
  );
}

function WorkflowPreview({ steps, compact }) {
  const list = (steps && steps.length ? steps : SUPPORTED);
  const flow = ["Created", ...list.filter((s) => !["draft"].includes(s.key)).map((s) => s.label || SUPPORTED_LABEL[s.key] || s.key), "Finalization"];
  return (
    <div className={`mt-3 flex flex-wrap items-center gap-1.5 ${compact ? "" : "p-3 rounded-lg bg-brand-bg border border-brand-line"}`} data-testid="workflow-preview" aria-label="Workflow path preview">
      {flow.map((label, i) => (
        <React.Fragment key={i}>
          <span className="inline-flex items-center rounded-md bg-white border border-brand-line px-2 py-1 text-xs font-medium text-brand-ink">{label}</span>
          {i < flow.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function WorkflowEditor({ type, onClose }) {
  const [steps, setSteps] = useState(() => (type.workflow_template?.steps || []).map((s) => ({ ...s })));
  const [name, setName] = useState(type.name);
  const [description, setDescription] = useState(type.description || "");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const original = React.useMemo(
    () => JSON.stringify({ steps: (type.workflow_template?.steps || []), name: (type.name || "").trim(), description: (type.description || "").trim() }),
    [type]);
  const dirty = JSON.stringify({ steps, name: name.trim(), description: description.trim() }) !== original;
  const present = new Set(steps.map((s) => s.key));
  const addable = SUPPORTED.filter((s) => !present.has(s.key));

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= steps.length) return;
    const copy = [...steps];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setSteps(copy);
  };
  const remove = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const add = (key) => { const s = SUPPORTED.find((x) => x.key === key); if (s) setSteps([...steps, { ...s }]); };
  const relabel = (i, label) => { const c = [...steps]; c[i] = { ...c[i], label }; setSteps(c); };
  const toggleApproval = (i) => { const c = [...steps]; c[i] = { ...c[i], is_approval: !c[i].is_approval }; setSteps(c); };

  const validate = () => {
    if (!name.trim()) return "A workflow name is required.";
    if (!present.has("draft")) return "The workflow must include a Draft (Created) step.";
    if (!steps.some((s) => s.is_approval)) return "At least one approval step is required (Finalization must be reachable).";
    if (!present.has("approved")) return "An Approved step is required so items can reach Finalization.";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await api.put(`/insight-types/${type.type_id}`, { name: name.trim(), description: description.trim(), workflow_steps: steps });
      toast.success("Workflow saved as a new version");
      setConfirm(false);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="workflow-editor">
      <button onClick={onClose} className="text-sm text-brand-civic hover:underline mb-3">← Back to workflows</button>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <h3 className="font-display font-semibold text-brand-ink mb-3">Basics</h3>
          <label className="block mb-3"><span className="text-xs font-semibold text-slate-500">Workflow name</span>
            <input data-testid="wf-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-500">Description</span>
            <textarea data-testid="wf-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={`${inputCls} h-auto py-2`} /></label>
          <p className="text-xs text-slate-400 mt-3">Current version: <strong>{type.workflow_template?.version ?? 1}</strong> · Used by {type.used_by_count ?? 0} item(s). Saving creates the next version; existing items keep their current version.</p>
        </Card>
        <Card>
          <h3 className="font-display font-semibold text-brand-ink mb-1">Workflow steps</h3>
          <p className="text-xs text-slate-500 mb-3">Reorder with the arrows (keyboard accessible). Approval steps require an Approver.</p>
          <ul role="list" className="space-y-2" data-testid="wf-steps">
            {steps.map((s, i) => (
              <li key={s.key} data-testid={`wf-step-${s.key}`} className="flex items-center gap-2 rounded-lg border border-brand-line bg-white px-3 py-2">
                <span className="text-xs font-mono text-slate-400 w-5">{i + 1}</span>
                <input aria-label={`Step ${i + 1} label`} value={s.label} onChange={(e) => relabel(i, e.target.value)} className="flex-1 h-8 rounded border border-brand-line px-2 text-sm outline-none focus:border-brand-civic" data-testid={`wf-step-label-${s.key}`} />
                <label className="flex items-center gap-1 text-xs text-slate-500"><input type="checkbox" checked={!!s.is_approval} onChange={() => toggleApproval(i)} data-testid={`wf-step-approval-${s.key}`} /> approval</label>
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${s.label} up`} data-testid={`wf-up-${s.key}`} className="p-1 text-slate-400 hover:text-brand-navy disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} aria-label={`Move ${s.label} down`} data-testid={`wf-down-${s.key}`} className="p-1 text-slate-400 hover:text-brand-navy disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => remove(i)} aria-label={`Remove ${s.label}`} data-testid={`wf-remove-${s.key}`} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
          {addable.length > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <select data-testid="wf-add-select" className={`${inputCls} h-9`} defaultValue="" onChange={(e) => { if (e.target.value) { add(e.target.value); e.target.value = ""; } }}>
                <option value="">Add a step…</option>
                {addable.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          )}
        </Card>
      </div>
      <Card className="mt-4">
        <h3 className="font-display font-semibold text-brand-ink mb-1">Preview</h3>
        <p className="text-xs text-slate-500 mb-2">This is how the path appears to staff on an item. Routing: Approve advances toward Finalization; Return for Revision goes back to the Creator; Deny ends the item as Denied.</p>
        <WorkflowPreview steps={steps} />
      </Card>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className={ghostBtn}>Cancel</button>
        <button data-testid="wf-save-btn" disabled={!dirty} onClick={() => { if (!dirty) { toast.info("No changes to save."); return; } const err = validate(); if (err) { toast.error(err); return; } setConfirm(true); }} className={primaryBtn}>Save New Version</button>
      </div>
      <Dialog open={confirm} onOpenChange={(o) => !o && setConfirm(false)}>
        <DialogContent data-testid="wf-activate-dialog">
          <DialogHeader>
            <DialogTitle>Activate {name} v{(type.workflow_template?.version ?? 1) + 1}?</DialogTitle>
            <DialogDescription>This version becomes the workflow for new items of this type. Existing items on version {type.workflow_template?.version ?? 1} will not be changed.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirm(false)} className={ghostBtn}>Cancel</button>
            <button data-testid="wf-activate-confirm" onClick={save} disabled={busy} className={primaryBtn}>Activate New Version</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateWorkflowDialog({ onClose }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim() || !code.trim()) { toast.error("Name and code are required."); return; }
    setBusy(true);
    try {
      await api.post("/insight-types", {
        name: name.trim(), code: code.trim().toUpperCase(), description: description.trim(),
        default_visibility: "staff_internal", workflow_steps: SUPPORTED.filter((s) => s.key !== "denied"),
      });
      toast.success("Workflow created (version 1)");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-workflow-dialog">
        <DialogHeader><DialogTitle>Create Workflow</DialogTitle><DialogDescription>Start with a standard Creator → Review → Approval → Finalization path. You can edit steps afterward.</DialogDescription></DialogHeader>
        <label className="block"><span className="text-xs font-semibold text-slate-500">Workflow name</span>
          <input data-testid="create-wf-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-500">Short code (e.g. CONTRACT)</span>
          <input data-testid="create-wf-code" value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-500">Description</span>
          <textarea data-testid="create-wf-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={`${inputCls} h-auto py-2`} /></label>
        <DialogFooter>
          <button onClick={onClose} className={ghostBtn}>Cancel</button>
          <button data-testid="create-wf-submit" onClick={create} disabled={busy} className={primaryBtn}>Create</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateDialog({ type, onClose }) {
  const [name, setName] = useState(`${type.name} (Copy)`);
  const [code, setCode] = useState(`${type.code}_COPY`);
  const [busy, setBusy] = useState(false);
  const dup = async () => {
    if (!name.trim() || !code.trim()) { toast.error("Name and code are required."); return; }
    setBusy(true);
    try {
      await api.post(`/insight-types/${type.type_id}/duplicate`, { name: name.trim(), code: code.trim().toUpperCase() });
      toast.success("Workflow duplicated (configuration only)");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Duplicate failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="duplicate-workflow-dialog">
        <DialogHeader><DialogTitle>Duplicate Workflow</DialogTitle><DialogDescription>Copies the configuration of "{type.name}" as a new workflow. No history or existing items are copied.</DialogDescription></DialogHeader>
        <label className="block"><span className="text-xs font-semibold text-slate-500">New name</span>
          <input data-testid="dup-wf-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></label>
        <label className="block"><span className="text-xs font-semibold text-slate-500">New code</span>
          <input data-testid="dup-wf-code" value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} /></label>
        <DialogFooter>
          <button onClick={onClose} className={ghostBtn}>Cancel</button>
          <button data-testid="dup-wf-submit" onClick={dup} disabled={busy} className={primaryBtn}>Duplicate</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
